import React, { useState, useMemo, useRef } from "react";
import { PackagePlus, Users } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { pad } from "./timeUtils";
import { computeLanes } from "./overlapLanes";
import { buildGroupPalette } from "./constants";
import { makeLongPressState, longPressHandlers, consumedLongPress } from "./touchLongPress";

// Format the Bali-local end time (HH:MM) for an appointment given its
// UTC start ISO + duration. Kept local to this file — DayGrid has its
// own copy because both grids build the tile time-range pill.
function endLocalTime(startIso, durationMin) {
  const [h, m] = new Date(startIso).toLocaleTimeString("en-GB", {
    hour: "2-digit", minute: "2-digit", timeZone: "Asia/Makassar", hour12: false,
  }).split(":");
  const startMin = parseInt(h, 10) * 60 + parseInt(m, 10);
  const endMin = startMin + (durationMin || 30);
  const hh = Math.floor(endMin / 60) % 24;
  const mm = endMin % 60;
  return `${pad(hh)}:${pad(mm)}`;
}

// Per-employee week grid. 7 date columns for the currently-selected staff;
// clicking a date header returns to the day view for that date. Click any
// empty cell to pre-fill the New Appointment modal for that staff & date.
// Drag-drop is intentionally disabled in this view (booked appointments still
// respond to right-click via the shared context menu). Row heights and column
// widths honour the shared vertical/horizontal zoom values.
export default function WeekStaffGrid({
  weekDates, slots, weekAppts,
  onSelectDate, onCellClick,
  colMinW, slotPx, resizePreview, colorForAppt, onApptClick, onApptContextMenu,
  // Iter-380 · Missing-Assistant alert — same contract as DayGrid.
  onApptAssignAssistant,
  // Packages catalog — used to resolve `package_id` → package name for the
  // hover tooltip on linked (package) appointments.
  packages = [],
  // Iter-278 · Services catalog powers the legacy zebra back-fill —
  // see DayGrid for the full rationale.
  services = [],
}) {
  // Shared long-press cursor: one active touch is all we track. Used to
  // pop the appointment context menu on tap-and-hold on tablets/phones
  // where a browser right-click is impossible.
  const lpState = useRef(makeLongPressState()).current;
  const dayName = (d) => d.toLocaleDateString("en-GB", { weekday: "short" });
  // IMPORTANT: use the LOCAL yyyy-mm-dd key (not UTC). In Asia/Makassar the
  // UTC representation of local-midnight shifts back one day, which would swap
  // column IDs by 1. Mirrors the `isoDate(d)` helper used elsewhere.
  const dayIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const today = new Date();
  const todayIso = dayIso(today);
  const minutesForDate = (d) => {
    // Bali minutes of `d`
    const [h, m] = new Date(d).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Makassar", hour12: false }).split(":");
    return parseInt(h, 10) * 60 + parseInt(m, 10);
  };
  const isSameDay = (isoStr, d) => {
    // Convert isoStr → Bali date components; compare with `d` (local).
    const parts = new Date(isoStr).toLocaleDateString("en-GB", { timeZone: "Asia/Makassar", year: "numeric", month: "2-digit", day: "2-digit" }).split("/");
    // en-GB → dd/mm/yyyy
    const key = `${parts[2]}-${parts[1]}-${parts[0]}`;
    return key === dayIso(d);
  };
  const template = `56px repeat(7, minmax(${colMinW}px, 1fr))`;
  // Sibling-glow state shared across all week cells (a package can span
  // multiple days for this staff member).
  const [hoveredLinkId, setHoveredLinkId] = useState(null);
  // Group scope hover — see DayGrid comments. Group hover extends across
  // the whole group AND every group member's package chain, and always
  // wins the visual hierarchy over the amber link ring.
  const [hoveredGroupScope, setHoveredGroupScope] = useState(null);
  const groupPalette = useMemo(() => buildGroupPalette(weekAppts), [weekAppts]);
  const apptIndex = useMemo(() => {
    const byGroup = new Map();
    const byLink = new Map();
    for (const a of weekAppts) {
      if (a.group_id) {
        if (!byGroup.has(a.group_id)) byGroup.set(a.group_id, []);
        byGroup.get(a.group_id).push(a);
      }
      if (a.link_group_id) {
        if (!byLink.has(a.link_group_id)) byLink.set(a.link_group_id, []);
        byLink.get(a.link_group_id).push(a);
      }
    }
    return { byGroup, byLink };
  }, [weekAppts]);
  const enterGroup = (appt) => {
    if (!appt || !appt.group_id) return;
    const set = new Set();
    const siblings = apptIndex.byGroup.get(appt.group_id) || [];
    for (const s of siblings) {
      set.add(s.id);
      if (s.link_group_id) {
        const chain = apptIndex.byLink.get(s.link_group_id) || [];
        for (const l of chain) set.add(l.id);
      }
    }
    setHoveredGroupScope({ groupId: appt.group_id, ids: set });
  };
  const leaveGroup = (gid) => {
    setHoveredGroupScope((prev) => (prev && prev.groupId === gid ? null : prev));
  };
  // Side-by-side split for double-booked tiles within each date column.
  const laneLayouts = useMemo(() => {
    const map = {};
    for (const d of weekDates) {
      const list = weekAppts.filter((a) => isSameDay(a.start_iso, d) && a.status !== "cancelled");
      map[dayIso(d)] = computeLanes(
        list,
        (a) => minutesForDate(a.start_iso),
        (a) => resizePreview[a.id] || a.duration_min || 30,
      );
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekDates, weekAppts, resizePreview]);
  return (
    <div className="min-w-full" data-testid="week-staff-grid">
      <div className="grid sticky top-0 bg-white z-10 border-b border-border" style={{ gridTemplateColumns: template }}>
        <div className="p-2 text-[10px] tracking-widest text-muted-foreground">TIME</div>
        {weekDates.map((d) => {
          const iso = dayIso(d);
          const isToday = iso === todayIso;
          return (
            <button
              key={iso}
              type="button"
              onClick={() => onSelectDate(d)}
              className={"p-2 text-xs font-medium border-l border-border truncate text-left hover:bg-muted/60 transition-colors " + (isToday ? "text-primary" : "")}
              data-testid={`week-date-header-${iso}`}
              title="Show this day for all staff"
            >
              <div className="text-[10px] uppercase tracking-wider opacity-70">{dayName(d)}</div>
              <div>{d.getDate()} {d.toLocaleDateString("en-GB", { month: "short" })}</div>
            </button>
          );
        })}
      </div>
      <div className="relative">
        {slots.map((minute) => {
          const hh = Math.floor(minute / 60);
          const mm = minute % 60;
          // Iter-227 — Time column labels render in solid black at
          // font-medium so the operator reads them sharply on every
          // display. :30 marks join :00 so half-hour ticks are legible.
          const label = mm === 0
            ? `${pad(hh)}:00`
            : (mm === 30 ? `${pad(hh)}:30` : "");
          return (
            <div
              key={minute}
              className={"grid border-b " + (mm === 0 ? "border-border" : "border-border/30")}
              style={{ gridTemplateColumns: template, height: slotPx }}
            >
              <div
                className="text-[10px] font-medium text-black px-2 pt-0.5"
                data-testid={`time-label-${pad(hh)}${pad(mm)}`}
              >
                {label}
              </div>
              {weekDates.map((d) => {
                // Anchor a tile in the 15-min slot whose range contains
                // its start (see DayGrid.jsx for the same fix). Without
                // this, appointments starting at odd minutes (e.g. 10:50
                // in a package chain whose previous sibling ran 30 min
                // from 10:20) fall between slots and never render.
                const cellAppts = weekAppts.filter((a) => {
                  if (!isSameDay(a.start_iso, d)) return false;
                  if (a.status === "cancelled") return false;
                  const bm = minutesForDate(a.start_iso);
                  return bm >= minute && bm < minute + 15;
                });
                return (
                  <button
                    key={dayIso(d) + "-" + minute}
                    type="button"
                    onClick={() => onCellClick(d, minute)}
                    className="relative border-l border-border/60 text-left transition-colors hover:bg-muted/50"
                    data-testid={`week-cell-${dayIso(d)}-${minute}`}
                  >
                    {cellAppts.map((a) => {
                      // Iter-278 — same legacy back-fill as DayGrid.
                      // Look up the service and fall back to its
                      // `processing_time_min` when the appointment row
                      // (booked pre-buffer) still carries a 0.
                      const svcDef = (services || []).find((s) => s.id === a.service_id);
                      const svcProcMin = Math.max(0, Number(svcDef?.processing_time_min || 0));
                      const apptProcMin = Math.max(0, Number(a.processing_time_min || 0));
                      const procMin = Math.max(apptProcMin, svcProcMin);
                      // Iter-387 · Fall back to the service's current
                      // Requires Assistant flag when a legacy appt
                      // doesn't carry the snapshot — same logic as
                      // DayGrid so the week view agrees.
                      const effRequiresAssistant = !!(a.requires_assistant || svcDef?.requires_assistant);
                      const legacyExtension = (apptProcMin === 0 && svcProcMin > 0) ? svcProcMin : 0;
                      const baseDur = Number(a.duration_min || 30) + legacyExtension;
                      const dur = resizePreview[a.id] || baseDur;
                      const height = (dur / 15) * slotPx - 2;
                      // Iter-380 · Same missing-assistant alert rule as
                      // DayGrid — shown on every column of the week
                      // view so a reception viewing anyone's week
                      // catches the gap.
                      // Iter-408 · Package booking fix — package tiles
                      // mirrored from a closed invoice are BORN with
                      // `status="completed"` (orders.py:_make_appt,
                      // is_closed_order branch), so the earlier
                      // `status !== "completed"` filter was swallowing
                      // the badge on every doctor's-column package
                      // tile that still had no assistant on record.
                      // Strict conditions: requires_assistant === true
                      // AND assist_staff_ids empty. Only cancelled
                      // rows are excluded. Covers single-service AND
                      // package tiles uniformly across all 8 branches.
                      const assistList = Array.isArray(a.assist_staff_ids) ? a.assist_staff_ids.filter(Boolean) : [];
                      const needsAssistant = effRequiresAssistant
                        && assistList.length === 0
                        && a.status !== "cancelled";
                      // Processing Time (Zebra Buffer) — mirrors the
                      // day-view rule. See DayGrid.jsx for the visual
                      // spec. Guest-facing surfaces receive only the
                      // service_time_min portion.
                      const zebraHeight = procMin > 0 ? (procMin / 15) * slotPx : 0;
                      const palette = colorForAppt(a);
                      const notes = (a.notes || "").trim();
                      const linkId = a.link_group_id || null;
                      const isLinked = !!linkId;
                      const isPackage = !!a.package_id;
                      const inGroupScope = !!(hoveredGroupScope && hoveredGroupScope.ids.has(a.id));
                      const groupTone = inGroupScope
                        ? groupPalette[hoveredGroupScope.groupId]
                        : null;
                      const isGlowing = !hoveredGroupScope && isLinked && linkId === hoveredLinkId;
                      const pkgName = isPackage
                        ? (packages.find((p) => p.id === a.package_id)?.name || null)
                        : null;
                      // Iter-255 · Transportation tiles share the same
                      // "no payment pill" rule as DayGrid.
                      const isTransport = !!a.is_transportation;
                      const showPayPill = !isTransport;
                      // Iter-189: mirror the DayGrid PAID logic so a
                      // linked-Closed/Paid order paints the tile pill
                      // as PAID even if `appointment.status` drifted.
                      const linkedPaid = a.order_status === "completed" || a.order_status === "closed" || a.order_payment_status === "paid";
                      const rawPayStatus = (a.status === "completed" || linkedPaid)
                        ? (a.payment_refunded ? "refunded" : "paid")
                        : "unpaid";
                      const isNoShow = a.status === "no_show";
                      const payStatus = (isNoShow && rawPayStatus === "unpaid") ? "no-show" : rawPayStatus;
                      const payStyle = payStatus === "paid"
                        ? "bg-emerald-500/20 text-emerald-300"
                        : payStatus === "refunded"
                          ? "bg-rose-500/20 text-rose-300"
                          : payStatus === "no-show"
                            ? "bg-amber-600/25 text-amber-200"
                            : "bg-amber-500/20 text-amber-300";
                      // Iter-246: start–end time chip. End derived from
                      // start_iso + duration_min so live resizes show
                      // the new range without a refetch.
                      const _timeFmtW = { timeZone: "Asia/Makassar", hour: "2-digit", minute: "2-digit", hour12: false };
                      const _startDtW = a.start_iso ? new Date(a.start_iso) : null;
                      const _endDtW = _startDtW && a.duration_min
                        ? new Date(_startDtW.getTime() + Number(a.duration_min) * 60000)
                        : (a.end_iso ? new Date(a.end_iso) : null);
                      const timeRange = (_startDtW && _endDtW)
                        ? `${_startDtW.toLocaleTimeString("en-GB", _timeFmtW)} – ${_endDtW.toLocaleTimeString("en-GB", _timeFmtW)}`
                        : "";
                      const lay = (laneLayouts[dayIso(d)] || {})[a.id] || { lane: 0, lanes: 1 };
                      // Percentage-based 20% right-column gap — matches
                      // DayGrid so the per-staff week view feels the
                      // same on every viewport.
                      const USABLE_COL_PCT = 80;
                      const laneWPct = USABLE_COL_PCT / lay.lanes;
                      const block = (
                        <div
                          key={a.id}
                          onClick={(e) => {
                            // Iter-268 — same click routing as DayGrid:
                            // single click opens View Appointment;
                            // long-press + right-click still fire the
                            // context menu (below). Iter-380 · When
                            // the tile is flagged for a missing
                            // assistant, short-circuit to the Edit
                            // path scrolled to the staff section.
                            e.stopPropagation();
                            if (consumedLongPress(lpState, a.id)) return;
                            if (needsAssistant && onApptAssignAssistant) {
                              onApptAssignAssistant(a);
                              return;
                            }
                            if (onApptClick) onApptClick(a);
                          }}
                          onContextMenu={(e) => onApptContextMenu(e, a)}
                          {...longPressHandlers(
                            lpState,
                            a.id,
                            (x, y) => onApptContextMenu(
                              { preventDefault: () => {}, stopPropagation: () => {}, clientX: x, clientY: y },
                              a,
                            ),
                          )}
                          onMouseEnter={() => {
                            if (a.group_id) enterGroup(a);
                            else if (isLinked) setHoveredLinkId(linkId);
                          }}
                          onMouseLeave={() => {
                            if (a.group_id) leaveGroup(a.group_id);
                            else if (isLinked) setHoveredLinkId((prev) => (prev === linkId ? null : prev));
                          }}
                          className={
                            "absolute rounded-md px-2 py-1 text-[11px] shadow-sm z-10 cursor-pointer overflow-hidden " +
                            "transition-[box-shadow,transform,background-color] duration-200 ease-out " +
                            (isGlowing ? "ring-2 ring-amber-400 shadow-lg shadow-amber-400/40 scale-[1.02] " : "") +
                            (inGroupScope ? "scale-[1.02] " : "")
                          }
                          style={{
                            // Sub-slot offset — see DayGrid.jsx for the
                            // rationale. Tiles that start at 10:50 render
                            // inside the 10:45 slot with 5-min of pixel
                            // padding from the slot top.
                            top: ((minutesForDate(a.start_iso) - minute) / 15) * slotPx + 1,
                            height,
                            left: `calc(${lay.lane * laneWPct}% + 2px)`,
                            width: `calc(${laneWPct}% - 4px)`,
                            backgroundColor: palette.bg,
                            color: palette.text,
                            boxShadow: inGroupScope && groupTone
                              ? `0 0 24px 6px ${groupTone.ring}, 0 0 8px 2px ${groupTone.ring}`
                              : undefined,
                            touchAction: "manipulation",
                          }}
                          data-testid={`week-appt-block-${a.id}`}
                          data-link-group-id={linkId || undefined}
                          data-group-id={a.group_id || undefined}
                          data-linked={isLinked ? "true" : undefined}
                          data-group-glowing={inGroupScope ? "true" : undefined}
                          data-glow-color={inGroupScope ? (groupTone?.key || undefined) : undefined}
                          data-processing-min={procMin > 0 ? procMin : undefined}
                        >
                          {zebraHeight > 0 && (
                            <div
                              className="absolute left-0 right-0 pointer-events-none rounded-b-md"
                              style={{
                                bottom: 0,
                                height: zebraHeight,
                                backgroundImage:
                                  "repeating-linear-gradient(45deg, rgba(90,90,90,0.55) 0 6px, rgba(255,255,255,0.15) 6px 12px)",
                                backgroundColor: "rgba(180,180,180,0.55)",
                                zIndex: 1,
                              }}
                              data-testid={`week-appt-zebra-${a.id}`}
                              aria-hidden="true"
                              title="Processing time (staff only)"
                            />
                          )}
                          <div className="font-medium truncate flex items-center gap-1">
                            <span
                              className="flex-shrink-0 text-[10px] font-bold text-black leading-tight"
                              data-testid={`week-appt-start-time-${a.id}`}
                              aria-label="Start time"
                            >
                              {new Date(a.start_iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Makassar", hour12: false })}
                            </span>
                            {isPackage && <PackagePlus className="w-3 h-3 flex-shrink-0 opacity-80" />}
                            {a.group_id && (
                              <Users
                                className="w-3 h-3 flex-shrink-0 text-indigo-700"
                                data-testid={`week-appt-group-icon-${a.id}`}
                                aria-label="In a group"
                                title="Part of a group booking"
                              />
                            )}
                            <span className="truncate">{a.customer_name || "Walk-in"}</span>
                            {a.guest_new && !a.checked_in_at && a.status !== "completed" && (
                              <span
                                className="ml-auto flex-shrink-0 rounded-full bg-amber-500 text-neutral-900 text-[8px] font-bold px-1.5 py-px leading-tight tracking-wider shadow-sm"
                                data-testid={`week-appt-new-badge-${a.id}`}
                                title="First-visit guest — no medical questionnaire on file yet"
                              >NEW</span>
                            )}
                            {a.status === "completed" && (
                              a.payment_refunded ? (
                                <span className="ml-auto flex-shrink-0 rounded-full bg-rose-600 text-white text-[8px] font-bold px-1.5 py-px leading-tight tracking-wider shadow-sm" data-testid={`week-appt-refunded-badge-${a.id}`}>REFUNDED</span>
                              ) : (
                                <span className="ml-auto flex-shrink-0 rounded-full bg-emerald-600 text-white text-[8px] font-bold px-1.5 py-px leading-tight tracking-wider shadow-sm" data-testid={`week-appt-paid-badge-${a.id}`}>PAID</span>
                              )
                            )}
                          </div>
                          <div className="opacity-90 truncate">{a.service_name}</div>
                          {/* Iter-380 · Missing-Assistant alert banner.
                              Same visual + behaviour as DayGrid; sits
                              inside each week-view tile so the alert is
                              visible across every column of the week. */}
                          {needsAssistant && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (onApptAssignAssistant) onApptAssignAssistant(a);
                              }}
                              className="absolute left-0 right-0 bottom-0 z-20 bg-amber-500 text-neutral-900 text-[9px] font-bold uppercase tracking-wider leading-tight py-[2px] px-1.5 text-center shadow-sm hover:bg-amber-400 transition-colors"
                              data-testid={`week-appt-assign-assistant-${a.id}`}
                              title="Assistant hasn't been assigned yet — click to assign"
                              aria-label="Assign Assistant"
                            >
                              <span aria-hidden="true">⚠</span> Assign Assistant
                            </button>
                          )}
                        </div>
                      );
                      return (
                        <Tooltip key={a.id}>
                          <TooltipTrigger asChild>{block}</TooltipTrigger>
                          <TooltipContent
                            side="right"
                            align="start"
                            className="max-w-xs bg-neutral-900 text-white whitespace-pre-wrap"
                            data-testid={`week-appt-tooltip-${a.id}`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium truncate">{a.customer_name || "Walk-in"}</div>
                                {a.service_name && (
                                  <div className="text-xs text-white/80 mt-0.5">{a.service_name}</div>
                                )}
                              </div>
                              {timeRange && (
                                <div
                                  className="text-[10px] text-white/60 font-mono whitespace-nowrap mt-[3px] tabular-nums"
                                  data-testid={`week-appt-tooltip-timerange-${a.id}`}
                                >
                                  {timeRange}
                                </div>
                              )}
                            </div>
                            <div className="mt-1.5">
                              {showPayPill ? (
                                <span
                                  className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${payStyle}`}
                                  data-testid={`week-appt-tooltip-payment-${a.id}`}
                                >
                                  {payStatus}
                                </span>
                              ) : (
                                <span
                                  className="inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-slate-500/20 text-slate-200"
                                  data-testid={`week-appt-tooltip-transport-${a.id}`}
                                >
                                  {a.status === "picked_up" ? "Picked Up" : "Transportation"}
                                </span>
                              )}
                            </div>
                            {isPackage && (
                              <div className="mt-1.5 flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-300">
                                <PackagePlus className="w-3 h-3" />
                                <span>Package{pkgName ? ` · ${pkgName}` : ""}</span>
                              </div>
                            )}
                            {notes && (
                              <>
                                <div className="text-[10px] uppercase tracking-wider text-white/60 mt-2 mb-0.5">Notes</div>
                                <div className="text-xs">{notes}</div>
                              </>
                            )}
                          </TooltipContent>
                        </Tooltip>
                      );
                    })}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
